import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_BASE } from '../config';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useAlert } from '../hooks/useAlert';
import { useGuestGate } from '../context/GuestGateContext';
import { SkeletonForm } from '../components/Skeleton';
import {
  PageHeader, SectionLabel, Chip, AmountChip, SummaryCard, SummaryRow, PayButton, PleaseWait,
} from '../components/Form';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Icon from '../components/Icon';
import { IconButton } from '../components/UI';
import FirstDepositBonusBanner from '../components/FirstDepositBonusBanner';

const WALLET_AMOUNT_OPTIONS = [5, 10, 20, 30, 40, 50, 100, 200, 500, 1000];
const WALLET_MIN_AMOUNT = 5;
const WALLET_MAX_AMOUNT = 1000;

interface PaymentMethod { id: string; name: string; paymentTag: string | null; logoUrl: string | null; gateway: string | null; }
interface PaymentAccount { id: string; label: string; deepLink: string; }

// Card (TheSolar) gateway: $10 floor instead of $5, hosted checkout instead of
// a deep-link account.
const CARD_MIN_AMOUNT = 10;
// The card processor skims 10%; the app compensates with a matching 10% bonus
// credit, so a $10 card deposit lands $11 in the wallet (the +10% is play-only).
const CARD_FEE_COMP_RATE = 0.1;
const fmtMoney = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

export default function WalletTopUp() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { alert } = useAlert();
  const { requireAuth } = useGuestGate();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPleaseWait, setShowPleaseWait] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payAccount, setPayAccount] = useState<PaymentAccount | null>(null);
  // Persona KYC verification link returned by TheSolar on a gated card deposit.
  // Non-null => show the "provider requires verification" modal.
  const [kycLink, setKycLink] = useState<string | null>(null);

  useEffect(() => {
    axios.get<PaymentMethod[]>(`${API_BASE}/payment-methods`)
      .then(res => {
        setPaymentMethods(res.data);
        if (res.data.length > 0) setSelectedMethod(res.data[0]);
      })
      .catch(() => alert(t('wallet.errorTitle'), t('wallet.loadMethodsFailed')))
      .finally(() => setLoading(false));
  }, [alert, t]);

  // Card (gateway) methods carry a $10 floor and drop the $5 chip; manual
  // methods keep the existing $5–$1000 range and deep-link flow.
  const isCard = selectedMethod?.gateway === 'thesolar';
  const minAmount = isCard ? CARD_MIN_AMOUNT : WALLET_MIN_AMOUNT;
  const amountOptions = isCard
    ? WALLET_AMOUNT_OPTIONS.filter(a => a >= CARD_MIN_AMOUNT)
    : WALLET_AMOUNT_OPTIONS;

  // The effective amount is the custom value when present, otherwise the
  // selected pill. Custom input is digits-only (no decimals possible), and
  // must land in the [min]–$1000 whole-dollar range.
  const effectiveAmount = customAmount.trim() ? parseInt(customAmount, 10) : selectedAmount;
  const isValidAmount =
    effectiveAmount != null &&
    Number.isInteger(effectiveAmount) &&
    effectiveAmount >= minAmount &&
    effectiveAmount <= WALLET_MAX_AMOUNT;

  const selectPill = (amt: number) => { setSelectedAmount(amt); setCustomAmount(''); };
  const onCustomChange = (raw: string) => { setCustomAmount(raw.replace(/[^0-9]/g, '')); setSelectedAmount(null); };

  const handleTopUp = async () => {
    if (!selectedMethod) return alert(t('wallet.missingInfoTitle'), t('wallet.selectMethodMessage'));
    if (!isValidAmount) return alert(t('wallet.selectAmountTitle'), isCard ? t('wallet.cardAmountRangeMessage') : t('wallet.amountRangeMessage'));
    if (isCard) return handleCardDeposit();
    setSubmitting(true);
    try {
      const res = await api.get<PaymentAccount>(`/payment-methods/${selectedMethod.id}/random-account`);
      setPayAccount(res.data);
      setShowPayModal(true);
    } catch (err: any) {
      alert(t('wallet.noPaymentAccountTitle'), err?.response?.data?.message || t('wallet.noPaymentAccountMessage'));
    } finally {
      setSubmitting(false);
    }
  };

  // Card path: hosted checkout pages refuse to be iframed (X-Frame-Options), so
  // we open them in a new tab. Pre-open the tab inside the click gesture (dodges
  // popup blockers), then point it at the checkout link. The deposit webhook
  // credits the wallet asynchronously.
  const handleCardDeposit = async () => {
    if (!selectedMethod || !isValidAmount) return;
    const checkoutWindow = window.open('', '_blank');
    setSubmitting(true);
    try {
      const res = await api.post<{ orderId: string; orderNumber: string; paymentLink: string }>('/orders/thesolar/deposit', {
        paymentMethodId: selectedMethod.id,
        amount: effectiveAmount,
        target: 'wallet',
      });
      const link = res.data?.paymentLink;
      if (!link) throw new Error('no link');
      if (checkoutWindow) checkoutWindow.location.href = link;
      else window.location.href = link;
    } catch (err: any) {
      if (checkoutWindow) { try { checkoutWindow.close(); } catch { /* ignore */ } }
      // TheSolar (inquiry KYC flow) can reject the deposit with a `kycLink` —
      // a Persona hosted verification page. Don't silently redirect: tell the
      // player the payment provider needs to verify them, then let them choose
      // to continue. The link opens on their explicit tap (popup-safe gesture).
      const link = err?.response?.data?.kycLink;
      if (typeof link === 'string' && link) {
        setKycLink(link);
        setSubmitting(false);
        return;
      }
      alert(t('wallet.orderErrorTitle'), err?.response?.data?.message || t('wallet.orderRecordFailed'));
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    setSelectedAmount(null);
    setCustomAmount('');
    setShowPleaseWait(true);
    setTimeout(() => {
      setShowPleaseWait(false);
      navigate('/profile?openHistory=1');
    }, 2500);
  };

  const handleConfirmPay = async () => {
    if (!payAccount || !selectedMethod || !isValidAmount) return;
    setShowPayModal(false);

    // Open the payment app's deep link directly inside the click gesture. A fresh,
    // user-activated navigation lets iOS/Android hand off https universal links
    // (cash.app, app.chime.com) to the native app — a pre-opened blank tab navigated
    // via location.href after the await below does NOT. Being inside the gesture also
    // keeps the popup-blocker from killing it.
    try { window.open(payAccount.deepLink, '_blank', 'noopener'); } catch { /* ignore */ }

    try {
      await api.post('/orders/topup', {
        paymentMethodId: selectedMethod.id,
        paymentAccountId: payAccount.id,
        amount: effectiveAmount,
        target: 'wallet',
        paymentSource: 'method',
      });
    } catch (err: any) {
      alert(t('wallet.orderErrorTitle'), err?.response?.data?.message || t('wallet.orderRecordFailed'));
      return;
    }

    setSelectedAmount(null);
    setCustomAmount('');
    setPayAccount(null);
    setShowPleaseWait(true);
    setTimeout(() => {
      setShowPleaseWait(false);
      navigate('/profile?openHistory=1');
    }, 2500);
  };

  const canPay = !!selectedMethod && isValidAmount;
  const customInvalid = customAmount.trim().length > 0 && !isValidAmount;

  // Card deposits get a 10% bonus that offsets the processor's 10% fee.
  const cardBonus = isCard && effectiveAmount ? effectiveAmount * CARD_FEE_COMP_RATE : 0;
  const cardCredited = isCard && effectiveAmount ? effectiveAmount + cardBonus : 0;

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <PageHeader
        icon="wallet"
        title={t('wallet.title')}
        right={<IconButton icon="arrow-left" accessibilityLabel={t('wallet.back')} onPress={() => navigate(-1)} />}
      />

      {loading ? (
        <div style={{ padding: 20 }}><SkeletonForm fields={3} /></div>
      ) : (
        <div style={{ padding: 20, paddingBottom: 40 }}>
          <FirstDepositBonusBanner variant="callout" />
          <SectionLabel>{t('wallet.steps.selectAmount')}</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {amountOptions.map(amt => (
              <AmountChip key={amt} amount={amt} active={selectedAmount === amt} onClick={() => selectPill(amt)} />
            ))}
          </div>

          <div style={{ fontSize: 13, fontWeight: 600, color: colors.textSub, marginTop: 16, marginBottom: 8 }}>
            {t('wallet.customAmountLabel')}
          </div>
          <div style={{
            display: 'flex', alignItems: 'center',
            backgroundColor: colors.card,
            border: `1.5px solid ${customInvalid ? '#ef4444' : colors.cardBorder}`,
            borderRadius: 12, minHeight: 50, paddingLeft: 14,
          }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: colors.textSub }}>$</span>
            <input
              inputMode="numeric"
              value={customAmount}
              onChange={e => onCustomChange(e.target.value)}
              placeholder={t('wallet.customAmountPlaceholder')}
              style={{
                flex: 1, padding: '13px 14px', fontSize: 15,
                color: colors.text, background: 'transparent', border: 'none', outline: 'none', width: '100%',
              }}
            />
          </div>
          <div style={{ fontSize: 12, color: customInvalid ? '#ef4444' : colors.textSub, marginTop: 6 }}>
            {isCard ? `Whole dollars only · $${CARD_MIN_AMOUNT}–$${WALLET_MAX_AMOUNT}` : t('wallet.customAmountHint')}
          </div>

          <SectionLabel>{t('wallet.steps.paymentMethod')}</SectionLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {paymentMethods.map(m => (
              <Chip
                key={m.id}
                active={selectedMethod?.id === m.id}
                onClick={() => setSelectedMethod(m)}
                icon={
                  m.logoUrl ? (
                    <img src={m.logoUrl} alt="" style={{ width: 18, height: 18, borderRadius: 4, objectFit: 'contain' }} />
                  ) : (
                    <Icon name="credit-card" size={13} color={selectedMethod?.id === m.id ? '#020617' : colors.textSub} />
                  )
                }
                label={m.name}
              />
            ))}
          </div>

          {canPay && (
            <SummaryCard title={t('wallet.summary.title')}>
              <SummaryRow label={t('wallet.summary.paymentMethod')} value={selectedMethod?.name || ''} />
              <SummaryRow label={t('wallet.summary.amount')} value={`$${effectiveAmount}`} />
              {isCard ? (
                <>
                  <SummaryRow label={t('wallet.summary.cardBonus')} value={`+$${fmtMoney(cardBonus)}`} />
                  <SummaryRow label={t('wallet.summary.walletCredit')} value={`$${fmtMoney(cardCredited)}`} />
                </>
              ) : (
                <SummaryRow label={t('wallet.summary.walletCredit')} value={t('wallet.summary.afterVerification')} />
              )}
            </SummaryCard>
          )}

          {isCard && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              backgroundColor: colors.accentSoft, borderRadius: 12, padding: 12, marginTop: 14,
            }}>
              <Icon name="info-circle" size={13} color={colors.accentText} style={{ marginTop: 2 }} />
              <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5, color: colors.accentText }}>
                {t('wallet.cardFeeNotice')}
              </div>
            </div>
          )}

          <PayButton label={isCard ? t('wallet.cardPayLabel') : t('wallet.topUpWallet')} onClick={() => requireAuth(handleTopUp)} loading={submitting} disabled={!canPay} />
        </div>
      )}

      <Modal open={showPayModal} onClose={() => setShowPayModal(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>{t('wallet.confirmTitle')}</div>
        <div style={{
          textAlign: 'center', padding: 20, borderRadius: 14,
          backgroundColor: colors.bg, border: `1px solid ${colors.cardBorder}`, marginBottom: 16,
        }}>
          <div style={{ fontSize: 36, fontWeight: 900, color: colors.text }}>${effectiveAmount?.toFixed(2)}</div>
          <div style={{ fontSize: 14, color: colors.textSub, marginTop: 4 }}>{t('wallet.via', { method: selectedMethod?.name })}</div>
          {payAccount && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
              <Icon name="link" size={12} color={colors.textSub} />
              <div style={{ fontSize: 13, fontWeight: 600, color: colors.textSub }}>{payAccount.label}</div>
            </div>
          )}
        </div>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 8,
          backgroundColor: colors.accentSoft, borderRadius: 12, padding: 12, marginBottom: 20,
        }}>
          <Icon name="info-circle" size={13} color={colors.accentText} style={{ marginTop: 2 }} />
          <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5, color: colors.accentText }}>
            {t('wallet.payNote')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label={t('wallet.cancel')} variant="outline" onClick={() => setShowPayModal(false)} />
          <Button label={t('wallet.openAndPay')} icon="external-link-alt" onClick={handleConfirmPay} />
        </div>
      </Modal>

      <Modal open={!!kycLink} onClose={() => setKycLink(null)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <Icon name="shield-alt" size={30} color={colors.accentText} />
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 8, textAlign: 'center' }}>
          {t('wallet.kycRequiredTitle')}
        </div>
        <div style={{ fontSize: 14, lineHeight: 1.5, color: colors.textSub, marginBottom: 20, textAlign: 'center' }}>
          {t('wallet.kycRequiredMessage')}
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label={t('wallet.cancel')} variant="outline" onClick={() => setKycLink(null)} />
          <Button
            label={t('wallet.kycVerifyButton')}
            icon="external-link-alt"
            onClick={() => {
              if (kycLink) { try { window.open(kycLink, '_blank', 'noopener'); } catch { /* ignore */ } }
              setKycLink(null);
            }}
          />
        </div>
      </Modal>

      <PleaseWait
        open={showPleaseWait}
        title={t('wallet.paymentRecordedTitle')}
        message={t('wallet.waitMessage')}
      />

    </div>
  );
}
